import type { RootState } from '@react-three/fiber';
import type { MapControls } from 'three-stdlib';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

import { CAMERA_VIEW } from '$viewer/constant';
import type { CameraView } from '$viewer/type';

export type CameraInfo = {
  position: [number, number, number];
  quaternion: [number, number, number, number];
  target: [number, number, number];
};

export type CameraScope = 'area' | 'client' | 'rcu';

// 카메라 저장을 허용하는 position/target 성분 절대값 및 ortho 거리의 상한(m)입니다.
// 컨트롤 maxDistance(1300m)는 카메라-타깃 거리 제한이라 팬으로 절대좌표가 커지는
// 것까지 막지는 못하므로, 정상 씬 콘텐츠 범위(원점 주변 수백 m~수 km) 대비 충분한
// 여유값으로 잡았습니다. Map 모드에서 지도 위도 상한 포화로 오염된 카메라(QA
// 재현값 39,469m)는 이 상한으로 걸러집니다. 오염값이 저장되면 새로고침 시 무검증 복원되어 씬이 보이지 않는 검은
// 화면(Reset View 만 탈출구)에 갇힙니다. (VPF-4561)
export const CAMERA_SAVE_COORDINATE_LIMIT = 10_000;

const isWithinSaveLimit = (component: number): boolean =>
  Number.isFinite(component) &&
  Math.abs(component) <= CAMERA_SAVE_COORDINATE_LIMIT;

const isSavableCameraInfo = (info: CameraInfo): boolean =>
  info.position.every(isWithinSaveLimit) &&
  info.quaternion.every(Number.isFinite) &&
  info.target.every(isWithinSaveLimit);

const isSavableOrthoDistance = (distance: number): boolean =>
  Number.isFinite(distance) &&
  distance > 0 &&
  distance <= CAMERA_SAVE_COORDINATE_LIMIT;

// persist 복원(hydrate) 경로의 슬롯 정화입니다. QA 장비 localStorage 에는 저장 가드
// 도입 전에 기록된 오염 카메라(상한 초과 좌표, NaN 이 JSON 직렬화로 null 이 된 성분)
// 가 이미 남아 있어, 쓰기 가드만으로는 복구되지 않고 복원 시점 힐링이 필요합니다.
const sanitizeCameraInfo = (info: Nullish<CameraInfo>): Nullish<CameraInfo> =>
  info != null && isSavableCameraInfo(info) ? info : null;

const sanitizeOrthoDistance = (distance: Nullish<number>): Nullish<number> =>
  distance != null && isSavableOrthoDistance(distance) ? distance : null;

export type CanvasStoreState = {
  cameraScope: CameraScope;
  cameraView: CameraView;
  canvasState?: RootState;
  control?: MapControls;
  // Map 모드 카메라는 transient(이탈 시 진입 스냅샷 복원)라 scope 슬롯 저장 대상이
  // 아닙니다. 저장 경로(VMapControls 'end', useCycleCameraView)가 이 플래그를 보고
  // 건너뛰도록 useGoogleMap 이 Map 모드 구간을 표시합니다. (VPF-4561)
  isMapMode: boolean;
  mousePos: null | { x: number; y: number; z: number }; // 마우스가 움직일때마다 xy평면 위의 좌표를 계산한다. z는 항상 0으로 고정
  orthoDistanceByScope: Record<CameraScope, Nullish<number>>;
  perspectiveCameraInfoByScope: Record<CameraScope, Nullish<CameraInfo>>;
};

const initialState: CanvasStoreState = {
  cameraScope: 'client',
  cameraView: CAMERA_VIEW.PERSPECTIVE,
  canvasState: undefined,
  isMapMode: false,
  mousePos: null,
  orthoDistanceByScope: { area: null, client: null, rcu: null },
  perspectiveCameraInfoByScope: { area: null, client: null, rcu: null },
};

export type CanvasStoreActions = {
  cycleCameraView: () => void;
  setCameraScope: (cameraScope: CameraScope) => void;
  setCanvasState: (canvasState: RootState) => void;
  setControl: (control: MapControls | undefined) => void;
  setIsMapMode: (isMapMode: boolean) => void;
  setLastOrthoDistance: (lastOrthoDistance: number) => void;
  setLastPerspectiveCameraInfo: (
    lastPerspectiveCameraInfo: CameraInfo,
    scope?: CameraScope,
  ) => void;
  setMousePos: (mousePos: null | { x: number; y: number; z: number }) => void;
};

export const useCanvasStore = createWithEqualityFn<
  CanvasStoreState & CanvasStoreActions
>()(
  persist(
    (set, get) => ({
      ...initialState,
      cycleCameraView: () => {
        const cameraView = get().cameraView;
        const nextCameraview = ((cameraView + 1) %
          Object.values(CAMERA_VIEW).length) as CameraView;

        if (!Object.values(CAMERA_VIEW).includes(nextCameraview)) {
          set({
            cameraView: CAMERA_VIEW.PERSPECTIVE,
          });
          return;
        }

        set({ cameraView: nextCameraview });
      },
      setCameraScope: (cameraScope: CameraScope) => set({ cameraScope }),
      setCanvasState: (canvasState: RootState) => {
        set({ canvasState });
      },
      setControl: (control: MapControls | undefined) => {
        set({ control });
      },
      setIsMapMode: (isMapMode: boolean) => {
        set({ isMapMode });
      },
      setLastOrthoDistance: (lastOrthoDistance: number) => {
        // 비유한·비양수·상한 초과 거리는 localStorage 에 남아 새로고침마다 복원되므로
        // 저장하지 않고 증적만 남깁니다. (VPF-4561)
        if (!isSavableOrthoDistance(lastOrthoDistance)) {
          console.warn('[canvasStore] 비정상 ortho 거리라 저장을 무시합니다', {
            lastOrthoDistance,
          });
          return;
        }

        const scope = get().cameraScope;
        set({
          orthoDistanceByScope: {
            ...get().orthoDistanceByScope,
            [scope]: lastOrthoDistance,
          },
        });
      },
      setLastPerspectiveCameraInfo: (
        lastPerspectiveCameraInfo: CameraInfo,
        // client→area viewer 전환 시 새 viewer 의 layout effect 가 cameraScope 를
        // 먼저 바꾼 뒤에야 이전 viewer 의 cleanup 이 돌므로, cleanup 이 셋업 시점
        // scope 를 캡처해 명시하면 현재 scope 와 무관하게 그 슬롯에 기록합니다. (VPF-4561)
        scope?: CameraScope,
      ) => {
        // 비유한 성분이나 상한 초과 좌표는 localStorage 에 남아 새로고침마다 복원돼
        // 검은 화면에 갇히므로 저장하지 않고 증적만 남깁니다. (VPF-4561)
        if (!isSavableCameraInfo(lastPerspectiveCameraInfo)) {
          console.warn(
            '[canvasStore] 비정상 카메라 정보라 저장을 무시합니다',
            lastPerspectiveCameraInfo,
          );
          return;
        }

        set({
          perspectiveCameraInfoByScope: {
            ...get().perspectiveCameraInfoByScope,
            [scope ?? get().cameraScope]: lastPerspectiveCameraInfo,
          },
        });
      },
      setMousePos: (mousePos: null | { x: number; y: number; z: number }) => {
        set({ mousePos });
      },
    }),
    {
      // 저장 슬롯이 이미 오염된 채 배포된 장비가 있어(위 CAMERA_SAVE_COORDINATE_LIMIT
      // 주석 참조) 복원 시점에 슬롯 단위로 정화합니다. 정상 슬롯은 그대로 보존해
      // 힐링이 과잉되지 않게 합니다. (VPF-4561)
      merge: (persistedState, currentState) => {
        const merged = {
          ...currentState,
          ...(persistedState as Nullish<Partial<CanvasStoreState>>),
        };

        return {
          ...merged,
          orthoDistanceByScope: {
            area: sanitizeOrthoDistance(merged.orthoDistanceByScope.area),
            client: sanitizeOrthoDistance(merged.orthoDistanceByScope.client),
            rcu: sanitizeOrthoDistance(merged.orthoDistanceByScope.rcu),
          },
          perspectiveCameraInfoByScope: {
            area: sanitizeCameraInfo(merged.perspectiveCameraInfoByScope.area),
            client: sanitizeCameraInfo(
              merged.perspectiveCameraInfoByScope.client,
            ),
            rcu: sanitizeCameraInfo(merged.perspectiveCameraInfoByScope.rcu),
          },
        };
      },
      name: 'canvas-store',
      version: 1,
      // rcu 는 매번 기본 뷰포인트로 시작하므로 카메라를 저장하지 않습니다.
      partialize: state => ({
        orthoDistanceByScope: { ...state.orthoDistanceByScope, rcu: null },
        perspectiveCameraInfoByScope: {
          ...state.perspectiveCameraInfoByScope,
          rcu: null,
        },
      }),
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useCanvasCameraView = () => {
  const cameraView = useCanvasStore(state => state.cameraView);
  const isTopView = cameraView === CAMERA_VIEW.TOP;
  const isPerspectiveView = cameraView === CAMERA_VIEW.PERSPECTIVE;
  const isFrontView = cameraView === CAMERA_VIEW.FRONT;
  const isSideView = cameraView === CAMERA_VIEW.SIDE;
  return {
    cameraView,

    isTopView,
    isPerspectiveView,
    isFrontView,
    isSideView,
  };
};

/**
 * xy평면 상에서의 마우스 좌표 리턴
 */
export const useCanvasMousePos = () => {
  return useCanvasStore(state => state.mousePos);
};
